import { BuyerProfile, SendInquiryProductInfo, Supplier } from '@/components/send-inquiry-dialog';
import {
  IApiAgentStartReq,
  IApiModifySearchCardReq,
  IApiSearchCardCondition,
} from '@/services/swagger/easourcing/models';
import { SelectSupplierInfo } from './comp-block';
import { SearchCardSubMsg, SearchCardV2SubMsg } from './chunk';
export * from './chunk';
export * from './type';

/**
 * 把供应商卡片选择的数据结构体转换成 inquiry 表单需要的初始化数据
 */
export const transSelectedSupplierToInquiryData = (selectedInfo: SelectSupplierInfo): Supplier => {
  const { subMsg } = selectedInfo;
  const { contentValue } = subMsg;
  const { originalProductDetailsDto, queryProduct } = contentValue;
  return {
    id: selectedInfo.subMsg.subMessageId,
    messageId: selectedInfo.messageId,
    subMessageId: selectedInfo.subMsg.subMessageId,
    defaultValues: {
      ...originalProductDetailsDto,
      productName: queryProduct,
      modelNo: originalProductDetailsDto?.model || '',
      specifications: originalProductDetailsDto?.specifications || '',
      orderQuantity: originalProductDetailsDto?.quantity || '',
      unitCode: originalProductDetailsDto?.quantity_unit || '',
      details: originalProductDetailsDto?.details || '',
      tradeTerms: originalProductDetailsDto?.trade_terms || '',
      preferredUnitPrice:
        originalProductDetailsDto?.unit_price != null ? String(originalProductDetailsDto.unit_price) : '',
      currency: originalProductDetailsDto?.unit_currency || '',
      maxBudget: originalProductDetailsDto?.max_budget != null ? String(originalProductDetailsDto.max_budget) : '',
      shippingMethod: originalProductDetailsDto?.shipping_method || '',
      paymentMethod: originalProductDetailsDto?.payment_method || '',
      validTo: originalProductDetailsDto?.valid_to || '',
      leadTime: '',
    } satisfies Supplier['defaultValues'],
    name: queryProduct,
    supplierArg: {
      ids: selectedInfo.selectFilter?.includeIds || [],
    },
  };
};

/**
 * 把 SearchCardSubMsg 转换成 SendInquiryDialogPortal 所需的 Supplier 数据
 * （无已选供应商信息时使用，messageId 与 supplierArg.ids 留空）
 */
export const transSubMsgToSupplier = (subMsg: SearchCardSubMsg): Supplier => {
  return transSelectedSupplierToInquiryData({
    messageId: '',
    subMsg,
    selectFilter: { includeIds: [] },
  });
};

/**
 * 把 SearchCardSubMsg + sessionId 转换成 agentStartPost 所需的请求参数
 */
export const transSubMsgToAgentStartReq = (subMsg: SearchCardSubMsg, sessionId?: string): IApiAgentStartReq => {
  const cv = subMsg.contentValue;
  const details = cv.originalProductDetailsDto;
  return {
    sessionId,
    buyerType: '',
    msgId: subMsg.subMessageId,
    productName: cv.queryProduct,
    model: details?.model,
    specification: details?.specifications,
    quantity: details?.quantity,
    unitCode: details?.quantity_unit,
    tradeTerms: details?.trade_terms,
    price: details?.unit_price != null ? String(details.unit_price) : undefined,
    currency: details?.unit_currency,
    maxBudget: details?.max_budget != null ? String(details.max_budget) : undefined,
    note: details?.notes ?? undefined,
    shippingMethod: details?.shipping_method,
    payMethod: details?.payment_method,
  };
};

/**
 * 把 SearchCardV2SubMsg 转换成 SendInquiryDialogPortal 所需的 Supplier 数据
 * （condition 字段扁平化，字段名为 camelCase）
 */
export const transSubMsgV2ToSupplier = (subMsg: SearchCardV2SubMsg): Supplier => {
  const cond = subMsg.contentValue.condition;
  return {
    id: subMsg.subMessageId,
    messageId: '',
    subMessageId: subMsg.subMessageId,
    defaultValues: {
      ...cond,
      productName: cond.queryProduct,
      modelNo: cond.model || '',
      specifications: cond.specifications || '',
      orderQuantity: cond.quantity || '',
      unitCode: cond.quantityUnit || '',
      details: cond.details || '',
      tradeTerms: cond.tradeTerms || '',
      preferredUnitPrice: cond.unitPrice != null ? String(cond.unitPrice) : '',
      currency: cond.unitCurrency || '',
      maxBudget: cond.maxBudget != null ? String(cond.maxBudget) : '',
      shippingMethod: cond.shippingMethod || '',
      paymentMethod: cond.paymentMethod || '',
      validTo: cond.validTo || '',
      leadTime: '',
    } satisfies Supplier['defaultValues'],
    name: cond.queryProduct,
    supplierArg: { ids: [] },
  };
};

/**
 * 把 SendInquiryDialog 中的表单数据（productInfo + buyerProfile + supplier）
 * 转换成 agentStartPost 所需的 IApiAgentStartReq 请求参数
 */
export const transFormDataToAgentStartReq = (
  productInfo: SendInquiryProductInfo,
  buyerProfile: BuyerProfile,
  supplier: Pick<Supplier, 'messageId' | 'subMessageId'>
): IApiAgentStartReq => {
  return {
    ...productInfo,
    productName: productInfo.productName,
    quantity: productInfo.orderQuantity,
    unitCode: productInfo.unitCode,
    tradeTerms: productInfo.tradeTerms || undefined,
    currency: productInfo.currency,
    price: productInfo.preferredUnitPrice,
    maxBudget: productInfo.maxBudget || undefined,
    specification: productInfo.specifications,
    model: productInfo.modelNo,
    note: productInfo.details,
    shippingMethod: productInfo.shippingMethod || undefined,
    payMethod: productInfo.paymentMethod || undefined,
    leadTime: productInfo.leadTime ? parseInt(productInfo.leadTime, 10) : undefined,
    productImages: [],
    userName: buyerProfile.name,
    companyName: buyerProfile.companyName,
    industry: buyerProfile.industry,
    introduction: buyerProfile.companyIntroduction,
    location: buyerProfile.location,
    msgId: supplier.subMessageId,
    sessionId: supplier.messageId,
  };
};

/**
 * 把 IApiAgentStartReq + 消息定位信息 转换成 modifySearchCardPost 所需的 IApiModifySearchCardReq 请求参数
 * @param req          来自 SendInquiryDialog 提交的 agentStart 参数
 * @param messageId    外层消息 ID（number 或可转换为数字的 string）
 * @param sessionId    会话 ID（number 或可转换为数字的 string）
 * @param subMessageId 子消息 ID
 */
export const transAgentStartReqToModifySearchCardReq = (
  req: IApiAgentStartReq,
  messageId: string | number,
  sessionId: string | number,
  subMessageId: string
): IApiModifySearchCardReq => {
  const condition: IApiSearchCardCondition = {
    ...req,
    queryProduct: req.productName || '',
    country: [],
    quantity: req.quantity || '',
    quantityUnit: req.unitCode || '',
    maxBudget: req.maxBudget || '',
    maxBudgetCurrency: req.currency || '',
    details: req.note || '',
    model: req.model || '',
    unitPrice: req.price || '',
    unitCurrency: req.currency || '',
    paymentMethod: req.payMethod || '',
    shippingMethod: req.shippingMethod || '',
    specifications: req.specification || '',
    tradeTerms: req.tradeTerms || '',
    validTo: '',
  };
  return {
    messageId: messageId as unknown as number,
    sessionId: sessionId as unknown as number,
    subMessageId,
    condition,
  };
};

/**
 * 把 SearchCardV2SubMsg + sessionId 转换成 agentStartPost 所需的请求参数
 */
export const transSubMsgV2ToAgentStartReq = (subMsg: SearchCardV2SubMsg, sessionId?: string): IApiAgentStartReq => {
  const cond = subMsg.contentValue.condition;
  const buyerProfile = subMsg.contentValue.buyerProfile;
  return {
    ...cond,
    sessionId,
    // 卡片参数没有这个字段，因此设为 undefined
    buyerType: buyerProfile?.buyerType || '',
    msgId: subMsg.subMessageId,
    productName: cond.queryProduct,
    model: cond.model,
    specification: cond.specifications,
    quantity: cond.quantity,
    unitCode: cond.quantityUnit,
    tradeTerms: cond.tradeTerms,
    price: cond.unitPrice || undefined,
    note: cond.details || undefined,
    currency: cond.unitCurrency,
    maxBudget: cond.maxBudget || undefined,
    shippingMethod: cond.shippingMethod,
    payMethod: cond.paymentMethod,
    userName: buyerProfile?.name || undefined,
    location: buyerProfile?.location || undefined,
    companyName: buyerProfile?.companyName || undefined,
    industry: buyerProfile?.industry || undefined,
    introduction: buyerProfile?.companyIntroduction || undefined,
  };
};
